<script lang="ts">
  import { pageTitle } from '../../lib/pageTitle';
  import { trackLead } from '#lib/analytics.ts';
  import { PUBLIC_API_BASE } from '#lib/publicUrls.ts';

  let activeForm: 'race' | 'rent' | null = null;

  let name = '';
  let email = '';
  let phone = '';
  let message = '';
  let submitting = false;
  let submitted = false;

  function resetForm() {
    name = '';
    email = '';
    phone = '';
    message = '';
    submitting = false;
    submitted = false;
  }

  function openForm(type: 'race' | 'rent') {
    resetForm();
    activeForm = type;
    submitted = false;
    window.setTimeout(() => {
      document.getElementById('racing-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }

  async function submitInquiry() {
    submitting = true;
    const source = activeForm === 'race' ? 'racing-participate' : 'racing-rental';
    const services_interested = activeForm === 'race' ? 'Racing - Participate' : 'Racing - Car Rental';

    try {
      const resp = await fetch(`${PUBLIC_API_BASE}/leads`, {
        method: 'post',
        headers: {
          'X-APP': '54d2629b-0a41-47ba-9857-d1b87527f803',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, phone, message, services_interested, source }),
      });

      if (resp.status === 200) {
        trackLead('racing', services_interested);
        submitted = true;
      } else {
        alert('There was an error with your request. Please try again later.');
      }
    } catch {
      alert('There was an error with your request. Please try again later.');
    } finally {
      submitting = false;
    }
  }
</script>

<svelte:head>
  <title>{pageTitle('Racing')}</title>
</svelte:head>

<div>
  <header
    class="masthead"
    style="background-image: url('/images/racing-bg.jpg');"
  >
    <div class="container">
      <div class="intro-text text-center">
        <div class="intro-lead-in">SpinFlip Racing</div>
        <h1 class="intro-heading text-uppercase">Built to Compete</h1>
        <p class="intro-subtitle">
          Experience the thrill of competitive motorsport with SpinFlip Racing.
        </p>
      </div>
    </div>
  </header>

  <section class="py-5">
    <div class="container">
      <div class="row text-center mb-5">
        <div class="col-lg-12">
          <h2 class="section-heading text-uppercase">The SpinFlip Racing Program</h2>
          <h3 class="section-subheading text-muted">
            From grassroots competition to professional endurance racing.
          </h3>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6 mb-4">
          <p>
            SpinFlip Racing brings the same engineering precision we apply to our
            professional services directly to the racetrack. Our program is built
            around competition-prepared vehicles, data-driven strategy, and a
            passion for motorsport that goes beyond the finish line.
          </p>
          <p>
            Whether you're a seasoned racer looking for seat time or a newcomer
            eager to experience wheel-to-wheel competition for the first time,
            SpinFlip Racing has an opportunity for you.
          </p>
        </div>
        <div class="col-md-6 mb-4">
          <p>
            Our vehicles are meticulously maintained and prepared with safety as
            the top priority. Full roll cages, racing harnesses, fire suppression
            systems, and all required safety equipment are standard across our fleet.
          </p>
          <p>
            We compete in endurance and sprint formats at tracks throughout the region,
            and we're always looking for talented and enthusiastic drivers to join us.
          </p>
        </div>
      </div>
    </div>
  </section>

  <section class="py-5 bg-dark">
    <div class="container">
      <div class="row text-center mb-5">
        <div class="col-lg-12">
          <h2 class="section-heading text-uppercase text-light">Get Involved</h2>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6 mb-4">
          <div class="cta-card">
            <span class="fa-stack fa-3x mb-3">
              <i class="fas fa-circle fa-stack-2x text-primary"></i>
              <i class="fas fa-flag-checkered fa-stack-1x fa-inverse"></i>
            </span>
            <h3 class="text-light">Want to Race?</h3>
            <p class="text-light-muted">
              Join our racing team as a driver. We welcome all experience levels,
              from first-timers to licensed competitors. We provide the car, the
              strategy, and the support — you bring the drive.
            </p>
            <ul class="text-light-muted text-start feature-list">
              <li>Full vehicle and safety equipment provided</li>
              <li>Pre-race coaching and track orientation</li>
              <li>Live telemetry and data review</li>
              <li>Sprint and endurance event opportunities</li>
            </ul>
            <button
              class="btn btn-primary btn-xl text-uppercase mt-3"
              on:click={() => openForm('race')}
            >
              I Want to Race
            </button>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="cta-card">
            <span class="fa-stack fa-3x mb-3">
              <i class="fas fa-circle fa-stack-2x text-primary"></i>
              <i class="fas fa-car fa-stack-1x fa-inverse"></i>
            </span>
            <h3 class="text-light">Rent the Racecar</h3>
            <p class="text-light-muted">
              Need a competition-ready vehicle for your next event? Our race cars
              are available for rental with flexible terms. Perfect for teams
              looking for a turn-key solution or individuals testing the waters.
            </p>
            <ul class="text-light-muted text-start feature-list">
              <li>Competition-prepared and tech-inspected</li>
              <li>Available for weekend or multi-day rentals</li>
              <li>Optional crew and pit support packages</li>
              <li>Competitive rates with insurance options</li>
            </ul>
            <button
              class="btn btn-primary btn-xl text-uppercase mt-3"
              on:click={() => openForm('rent')}
            >
              Inquire About Rental
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>

  {#if activeForm}
    <section id="racing-form" class="py-5">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            {#if submitted}
              <div class="text-center py-4">
                <span class="fa-stack fa-4x mb-3">
                  <i class="fas fa-circle fa-stack-2x text-primary"></i>
                  <i class="fas fa-check fa-stack-1x fa-inverse"></i>
                </span>
                <h3>Thank you for your interest!</h3>
                <p>We'll be in touch with more details shortly.</p>
                <button
                  class="btn btn-primary text-uppercase mt-3"
                  on:click={() => { activeForm = null; submitted = false; }}
                >
                  Close
                </button>
              </div>
            {:else}
              <h3 class="text-center mb-4">
                {activeForm === 'race' ? 'Racing Inquiry' : 'Rental Inquiry'}
              </h3>
              <form on:submit|preventDefault={submitInquiry}>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <div class="form-group">
                      <label for="race-name">Your Name *</label>
                      <input class="form-control" id="race-name" type="text" placeholder="Your Name" required bind:value={name} />
                    </div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <div class="form-group">
                      <label for="race-email">Your Email *</label>
                      <input class="form-control" id="race-email" type="email" placeholder="Your Email" required bind:value={email} />
                    </div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <div class="form-group">
                      <label for="race-phone">Your Phone *</label>
                      <input class="form-control" id="race-phone" type="tel" placeholder="Your Phone" required bind:value={phone} />
                    </div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <div class="form-group">
                      <label for="race-message">
                        {activeForm === 'race' ? 'Tell us about your racing experience' : 'Tell us about your event and needs'} *
                      </label>
                      <textarea class="form-control" id="race-message" placeholder={activeForm === 'race' ? 'Your experience level, events of interest...' : 'Event dates, track, duration...'} required bind:value={message}></textarea>
                    </div>
                  </div>
                  <div class="col-12 text-center">
                    <button class="btn btn-primary btn-xl text-uppercase" type="submit" disabled={submitting}>
                      {submitting ? 'Sending...' : 'Submit Inquiry'}
                    </button>
                    <button class="btn btn-outline-secondary btn-xl text-uppercase ms-3" type="button" on:click={() => { activeForm = null; }}>
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            {/if}
          </div>
        </div>
      </div>
    </section>
  {/if}
</div>

<style>
  .masthead {
    height: 100vh;
    background-size: cover;
    background-position: center;
  }

  .intro-text {
    /* Clear fixed nav without the old 200px + main offset stack */
    padding-top: clamp(5rem, 16vh, 7.5rem);
    padding-bottom: clamp(3rem, 10vh, 6.25rem);
    color: white;
    /* The photo is bright behind the copy; keep it legible */
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.75);
  }

  .intro-subtitle {
    font-size: 1.25rem;
    font-style: italic;
    color: rgba(255, 255, 255, 0.85);
    margin-top: 1rem;
  }

  .cta-card {
    text-align: center;
    padding: 2.5rem 2rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    transition: border-color 0.3s ease;
  }

  .cta-card:hover {
    border-color: #fed136;
  }

  .text-light-muted {
    color: rgba(255, 255, 255, 0.7);
  }

  .feature-list {
    list-style: none;
    padding-left: 0;
  }

  .feature-list li {
    padding: 0.25rem 0;
  }

  .feature-list li::before {
    content: '\f00c';
    font-family: 'Font Awesome 6 Free';
    font-weight: 900;
    color: #fed136;
    margin-right: 0.5rem;
  }

  #racing-form .form-group {
    margin-bottom: 1rem;
  }

  #racing-form textarea.form-control {
    height: 120px;
  }
</style>
